/* eslint-disable react-hooks/exhaustive-deps */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import './cron-builder.css';
import React, { useEffect, useState } from 'react';
import cronstrue from 'cronstrue/i18n';
import { metadata, loadHeaders, DefaultCronExpression } from '@/constants/cron/cron';
import { CronProp, State, HeaderValType } from '@/types/cron';
import { Separator } from '@/components/ui/separator';
import { INPUT_FIELD_CLASSNAME } from '@/constants/classname/classname';
import { DatePicker } from '@/components/ui/date-picker-input';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/redux';
import { workflowActions } from '@/redux/slice/workflowSlice';
import { createDateString } from '@/helpers/date';
import { getNextRuns } from './helpers/getNextRuns';
import { formatDateTime } from '@/helpers/billing';
import { showNotification } from '@/services/notificationService';

export const Cron: React.FunctionComponent<CronProp> = (props) => {
  const dispatch: AppDispatch = useDispatch()
  const { create } = useSelector((state: RootState) => state.workflow)
  const [state, setState] = useState<State>({
    value: [],
    headers: loadHeaders(props.options),
    locale: props.locale ? props.locale : 'en'
  })

  useEffect(() => {
    if (!state.selectedTab || state.value.join(' ') === DefaultCronExpression) {
      tabChanged('Daily')
    }
  }, [state.selectedTab])

  useEffect(() => {
    let newVal = '';
    newVal = state.value.toString().replace(/,/g, ' ');
    newVal = newVal.replace(/!/g, ',');
    if (props.value !== newVal) {
      setValue(props.value ? props.value : '')
    }
    if (props.translateFn && !props.locale) {
      console.warn('Warning !!! locale not set while using translateFn');
    }
  }, [props.value])

  useEffect(() => {
    state.value && state.value.length && parentChange(state.value)
  }, [state.value])

  const setValue = (value: string) => {
    const prevState = { ...state };
    prevState.value = value.replace(/,/g, '!').split(' ');
    const allHeaders = loadHeaders();

    // Normalize value
    if (value && value.split(' ').length === 6) {
      prevState.value.push('*');
    }
    if (!value || value.split(' ').length !== 7) {
      value = DefaultCronExpression;
      prevState.selectedTab = allHeaders[0]; // Default = Daily
      prevState.value = value.split(' ');
      parentChange(value.split(' '));
    } else {
      prevState.value = value.replace(/,/g, '!').split(' ');
    }

    const val = prevState.value;

    // DETECTION ORDER (most specific → least specific):
    // 1. Minutes (val[1] has /)
    // 2. Hourly (val[2] has /)
    // 3. Weekly (specific day-of-week)
    // 4. Daily with interval (1/N patterns)
    // 5. Daily every day (val[3] is * with specific time)
    // 6. Monthly (specific day number)

    // 1. Minutes - minute field has interval pattern (e.g., 0/15, 0/30, 0/45)
    if (
      val[1] !== '0' &&
      val[1] !== '*' &&
      val[1].includes('/')
    ) {
      prevState.selectedTab = allHeaders.find(h => h === 'Minutes') || allHeaders[0];

      // 2. Hourly - hour field has interval pattern (e.g., 0/2, 0/3, 0/6)
    } else if (
      val[2] !== '0' &&
      val[2] !== '*' &&
      val[2].includes('/')
    ) {
      prevState.selectedTab = allHeaders.find(h => h === 'Hourly') || allHeaders[1];

      // 3. Weekly - specific day-of-week set (e.g., MON, TUE, etc.)
    } else if (val[5] !== '?' && val[5] !== '*') {
      prevState.selectedTab = allHeaders.find(h => h === 'Weekly') || allHeaders[2];

      // 4. Daily with interval - day-of-month has 1/N pattern (e.g., 1/2, 1/3, 1/7)
    } else if (
      val[3].startsWith('1/') &&
      val[4] === '*' &&
      val[5] === '?'
    ) {
      prevState.selectedTab = allHeaders.find(h => h === 'Daily') || allHeaders[0];

      // 5. Daily every day - day-of-month is * or 1/1 with specific time
    } else if (
      (val[3] === '*' || val[3] === '1/1') &&
      val[4] === '*' &&
      val[5] === '?' &&
      val[1] === '0' &&
      val[2] === '0'
    ) {
      prevState.selectedTab = allHeaders.find(h => h === 'Daily') || allHeaders[0];

      // 6. Monthly - specific day of month (e.g., 15, 20, 31)
    } else if (
      val[3] !== '?' &&
      val[3] !== '*' &&
      !val[3].startsWith('1/') &&
      val[4] === '1/1'
    ) {
      prevState.selectedTab = allHeaders.find(h => h === 'Monthly') || allHeaders[3];

      // Fallback - should never reach here if all patterns are covered
    } else {
      showNotification(
        { data: undefined, err: 'Unrecognized cron pattern. Defaulting to Daily.' },
        'Unrecognized cron pattern. Defaulting to Daily.',
        {}
      );
      prevState.selectedTab = allHeaders[0];
    }

    // Default (no header found)
    if (!prevState.headers.includes(prevState.selectedTab)) {
      prevState.selectedTab = prevState.headers[0];
    }

    setState(prevState);
  };

  const tabChanged = (tab: HeaderValType) => {
    if (state.selectedTab !== tab && !props.disabled) {
      setState({ ...state, selectedTab: tab, value: defaultValue(tab) })
    }
  }

  const onValueChange = (val: string[]) => {
    if (val && val.length) {
      setState({ ...state, value: [...val] });
    } else {
      val = ['0', '0', '00', '1/1', '*', '?', '*']
      setState({ ...state, value: val });
    }
  }

  const parentChange = (val: string[]) => {
    let newVal = '';
    newVal = val.toString().replace(/,/g, ' ');
    newVal = newVal.replace(/!/g, ',');
    props.onChange(newVal, getVal())
  }

  const getVal = () => {
    const val = cronstrue.toString(
      state.value.toString().replace(/,/g, ' ').replace(/!/g, ','), {
      throwExceptionOnParseError: false, locale: state.locale
    })
    if (val.search('undefined') === -1 && state.value && state.value.length) {
      return val;
    }
    return '-';
  }

  const defaultValue = (tab: HeaderValType): string[] => {
    const defaultValCron = metadata.find(m => m.name == tab)
    if (!defaultValCron || !defaultValCron.initialCron) {
      return DefaultCronExpression.split(' ');
    }
    return defaultValCron.initialCron;
  }

  const getComponent = (tab: HeaderValType) => {
    const index = state.headers.indexOf(tab);
    let selectedMetaData = metadata.find(data => data.name === tab);
    if (!selectedMetaData) {
      selectedMetaData = metadata[index];
    }
    if (!selectedMetaData) {
      throw new Error('Value does not match any available headers.');
    }
    const CronComponent = selectedMetaData.component;
    return <CronComponent
      translate={translate}
      value={state.value}
      onChange={onValueChange}
      disabled={props.disabled}
    />;
  }

  const translate = (key: string): string => {
    let translatedText = key;
    if (props.translateFn) {
      translatedText = props.translateFn(key);
      if (typeof translatedText !== 'string') {
        throw new Error('translateFn expects a string translation');
      }
    }
    return translatedText;
  }

  const getEmptyComponent = () => {
    return (
      <div className='py-4 flex flex-col items-start w-full'>
        <h4 className='text-base font-semibold'>
          Schedule your workflow runs
        </h4>
        <p className='text-sm text-secondary'>
          Click on the tabs above to add Schedule methods
        </p>
      </div>
    )
  }

  const buildStartDateTime = () => {
    try {
      const baseDate = new Date(create.executionStartDate);
      let hours = Number(create.executionStartHour);
      const minutes = Number(create.executionStartMinute);

      if (create.amPm === 'PM' && hours < 12) hours += 12;
      if (create.amPm === 'AM' && hours === 12) hours = 0;

      baseDate.setHours(hours, minutes, 0, 0);
      return baseDate;
    } catch (err) {
      console.error('Invalid execution start date/time:', err);
      return new Date();
    }
  };

  return (
    <div className='cron_builder text-primary'>
      <h4 className='text-base font-semibold text-primary text-left mb-2'>
        Frequency
      </h4>
      <Select
        value={state.selectedTab}
        defaultValue='Daily'
        onValueChange={(e: HeaderValType) => tabChanged(e)}
      >
        <SelectTrigger className='w-full'>
          <SelectValue placeholder='Select a Frequency' />
        </SelectTrigger>
        <SelectContent className='bg-background'>
          {state.headers.map((header, i) => (
            <SelectItem className='text-primary' value={header} key={header + i}>
              {translate(header)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className='cronBuilderElement'>
        {state.selectedTab ? getComponent(state.selectedTab) : getEmptyComponent()}
      </div>
      {props.showResultText && getVal()?.length > 2 ? (
        <>
          <Separator className='my-4' />
          <div className='mb-4'>
            <h4 className='text-left text-base font-semibold text-left text-primary'>
              Start Time
            </h4>
            <p className='text-secondary font-normal leading-4 text-sm text-left mb-2'>
              The schedule will start running after this date and time.
            </p>
            <div className='flex items-center flex-wrap gap-2'>
              <DatePicker
                date={new Date(create.executionStartDate + 'T12:00:00')}
                setDate={(d) => {
                  if (!d || typeof d === 'function') return
                  const year = d.getFullYear()
                  const month = String(d.getMonth() + 1).padStart(2, '0')
                  const day = String(d.getDate()).padStart(2, '0')
                  dispatch(workflowActions.setExecutionStartDate(`${year}-${month}-${day}`))
                }}
                isDisabled={false}
                className={INPUT_FIELD_CLASSNAME + ' w-[220px]'}
              />
              <input
                className={INPUT_FIELD_CLASSNAME.replaceAll('w-full ', 'w-[80px] ')}
                type='number'
                value={create.executionStartHour}
                onChange={(e) => dispatch(workflowActions.setExecutionHour(Number(e.target.value)))}
                min={1}
                max={12}
              />
              <div className='mx-1'>:</div>
              <input
                className={INPUT_FIELD_CLASSNAME.replaceAll('w-full ', 'w-[80px] ')}
                type='number'
                value={create.executionStartMinute}
                onChange={(e) => dispatch(workflowActions.setExecutionMinute(Number(e.target.value)))}
                min={0}
                max={59}
              />
              <Select
                value={create.amPm}
                onValueChange={(e) => dispatch(workflowActions.setAmPm(String(e)))}
              >
                <SelectTrigger className='w-[80px]'>
                  <SelectValue placeholder='AM/PM' />
                </SelectTrigger>
                <SelectContent className='bg-background text-primary'>
                  <SelectItem value='AM'>AM</SelectItem>
                  <SelectItem value='PM'>PM</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={create.timezone}
                onValueChange={(e) => dispatch(workflowActions.setTimezone(String(e)))}
              >
                <SelectTrigger className='w-[100px]'>
                  <SelectValue placeholder='Timezone' />
                </SelectTrigger>
                <SelectContent className='bg-background text-primary'>
                  <SelectItem value='PST'>PST</SelectItem>
                  <SelectItem value='EST'>EST</SelectItem>
                  <SelectItem value='IST'>IST</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator className='my-4' />

          <div className='text-base text-left'>
            <h4 className='font-semibold mb-1'>Summary</h4>
            <p>
              This workflow will run{' '} <span className='text-accent font-semibold'>{getVal()}</span>, starting on{' '}
              <span className='text-accent font-semibold'>
                {createDateString(
                  create.executionStartDate,
                  create.executionStartHour,
                  create.executionStartMinute,
                  create.amPm
                ).displayValue}{' '}
                {create.timezone}
              </span>.
            </p>
          </div>
        </>
      ) : null}
      <Separator className='my-4' />
      {getNextRuns(
        props.value ? props.value : DefaultCronExpression,
        5,
        { startDate: buildStartDateTime() }
      ).length > 0 && (
          <div className='text-base text-left'>
            <h4 className='font-semibold mb-2'>Next 5 Runs</h4>
            <ul className='list-disc list-inside text-sm text-primary'>
              {getNextRuns(
                props.value ? props.value : DefaultCronExpression,
                5,
                { startDate: buildStartDateTime() }
              ).map((date, index) => (
                <li key={index}>{formatDateTime(date as unknown as string)} {create.timezone}</li>
              ))}
            </ul>
          </div>
        )}
      {/* 
        TODO: Update This
        Do we really want to show CRON expression in the UI?
        
        {props.showResultCron && getVal()?.length && (
            <div className='text-base'>
                {state.value.toString().replace(/,/g, ' ').replace(/!/g, ',')}
            </div>
        )} 
      */}
    </div>)
}
